// Interceptor global de fetch — Sessão 17, hardening de auth parte C2.
//
// Quando uma chamada à API do Qaa.sa recebe 401, tenta renovar o access
// token via /auth/refresh e refaz a requisição UMA vez. Se o refresh
// falhar, encerra a sessão e volta ao login.
//
// É transparente: envolve o window.fetch uma única vez (instalado no
// index.jsx, antes do React renderizar), então NENHUM dos ~21 arquivos
// que fazem fetch precisa mudar.

import API_URL from '../config';
import authService from '../services/authService';
import { flushDraftToStorage } from './ncDraftStore'; // #145

// Endpoints de auth que NÃO devem disparar refresh-em-401:
//  - /auth/login   → 401 = credencial errada (não é sessão expirada)
//  - /auth/refresh → evita recursão
//  - /auth/logout  → best-effort; não faz sentido renovar para deslogar
function _isNoRefreshEndpoint(url) {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
}

// Sessão 19 — Single-flight movido para `authService.refreshAccessToken()`
// (singleton). Antes existia um `_refreshing` local AQUI, separado do
// equivalente no axios interceptor (api.js); requisições paralelas fetch
// + axios podiam disparar dois refreshes independentes em paralelo,
// invalidando um ao outro via rotation. Agora ambos compartilham a
// mesma promise pendente.

// Refresh falhou (refresh token expirado/revogado) → encerra a sessão.
function _encerrarSessao(response) {
  flushDraftToStorage(); // #145 — preserva rascunho antes do logout forçado
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refresh_token');
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
  return response;
}

export function installFetchInterceptor() {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const response = await originalFetch(input, init);

    // Só intervém em: 401 + chamada à API + URL string + não-retry +
    // endpoint elegível + havendo refresh token guardado.
    if (
      response.status !== 401 ||
      !url.startsWith(API_URL) ||
      typeof input !== 'string' ||
      init.__isRetry ||
      _isNoRefreshEndpoint(url) ||
      !authService.getRefreshToken()
    ) {
      return response;
    }

    // Renova o access token (single-flight no authService — compartilhado
    // com o axios interceptor em api.js).
    try {
      await authService.refreshAccessToken();
    } catch (e) {
      return _encerrarSessao(response);
    }

    // Refaz a requisição original com o novo access token.
    // __isRetry garante que um 401 no retry não dispare outro refresh.
    const novoToken = authService.getToken();
    return originalFetch(input, {
      ...init,
      __isRetry: true,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${novoToken}`,
      },
    });
  };
}
